import { Card } from '../components/ui';
import { dayName, shortDate } from '../components/ui';
import type { DashboardModuleProps } from './registry';

const T = {
  en: {
    title: 'Where the plan stands',
    nothingBought: 'Nothing bought yet — the whole week is open to plan.',
    boughtUpTo: (day: string) => `Shopped. Meals up to ${day} are set by what came home.`,
    boughtNoDay: 'Shopped. The meals now have to work with what came home.',
    nextShop: (shop: string, day: string) => `Next shop: ${shop}, ${day}.`,
    noNextShop: 'No further shop planned this week.',
    planTo: (shop: string, day: string) => `So the job now is the days up to ${shop} on ${day}.`,
    settled: (n: number) => `${n} settled`,
    open: (n: number) => `${n} still open`,
    settledHelp:
      'Settled meals can still be moved or swapped, but only for something the shopping already covers.',
    openHelp: 'Open meals are waiting on a shop, so they are free to be anything.',
    allSettled: 'Every meal this week is covered by shopping already done.',
  },
  fr: {
    title: 'Où en est le plan',
    nothingBought: "Rien d'acheté pour l'instant — toute la semaine est à planifier.",
    boughtUpTo: (day: string) => `Courses faites. Les repas jusqu'à ${day} dépendent de ce qui a été acheté.`,
    boughtNoDay: 'Courses faites. Les repas doivent maintenant tenir avec ce qui a été acheté.',
    nextShop: (shop: string, day: string) => `Prochaines courses : ${shop}, ${day}.`,
    noNextShop: 'Pas d’autres courses prévues cette semaine.',
    planTo: (shop: string, day: string) => `Il reste donc les jours jusqu'à ${shop} le ${day}.`,
    settled: (n: number) => `${n} calés`,
    open: (n: number) => `${n} encore ouverts`,
    settledHelp:
      'Les repas calés peuvent être déplacés ou changés, mais seulement pour ce que les courses permettent déjà.',
    openHelp: 'Les repas ouverts attendent des courses : ils peuvent encore être n’importe quoi.',
    allSettled: 'Tous les repas de la semaine sont couverts par les courses déjà faites.',
  },
} as const;

/**
 * The frame for the whole planning page: you are planning up to the next shop.
 *
 * Once the shop is done, the meals it covers are settled — they can be moved
 * around but not turned into something nobody bought. What is genuinely still
 * to decide are the days beyond it, plus anything a specialist trip later in
 * the week is still bringing (§43-44).
 */
export function HorizonModule({ week, language }: DashboardModuleProps) {
  const t = T[language];
  if (!week) return null;

  const { horizon } = week;
  const when = (date: string) => `${dayName(date, language)} ${shortDate(date, language)}`;

  const state = horizon.lastShop ? 'shopped' : 'open';

  return (
    <Card title={t.title}>
      <div className="stack" style={{ gap: 10 }}>
        <p className={`banner${state === 'shopped' ? '' : ' info'}`} style={{ margin: 0 }}>
          {state === 'shopped'
            ? horizon.plannedTo
              ? t.boughtUpTo(when(horizon.plannedTo))
              : t.boughtNoDay
            : t.nothingBought}
        </p>

        <p style={{ margin: 0 }}>
          {horizon.nextShop
            ? t.nextShop(horizon.nextShop.shopLabel, when(horizon.nextShop.date))
            : t.noNextShop}
        </p>

        {horizon.openDishes > 0 && horizon.nextShop && (
          <p className="dim" style={{ margin: 0 }}>
            {t.planTo(horizon.nextShop.shopLabel, when(horizon.nextShop.date))}
          </p>
        )}

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="pill settled">{t.settled(horizon.settledDishes)}</span>
          <span className="pill open">{t.open(horizon.openDishes)}</span>
        </div>

        <p className="dim" style={{ margin: 0, fontSize: 13 }}>
          {horizon.openDishes === 0 && horizon.settledDishes > 0
            ? t.allSettled
            : horizon.settledDishes > 0
              ? t.settledHelp
              : t.openHelp}
        </p>
      </div>
    </Card>
  );
}
